import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import mixpanel from 'mixpanel-browser';
import Hotjar from '@hotjar/browser';

import AnalyticsContext from './context';

import { millisToMinutesAndSeconds } from '../../utils/date';

const getCurrentUser = () => JSON.parse(localStorage.getItem('user')) || {};

const AnalyticsContextProvider = ({ children }) => {
  const [lastPath, setLastPath] = useState('');
  const [lastPage, setLastPage] = useState({});

  const isProduction = import.meta.env.VITE_ENV === 'PRODUCTION';

  // MIXPANEL
  const normalizeSessionData = (userSession) => ({
    client_id: userSession.clientId,
    client_company: userSession.company,
    client_paying: userSession.paying,
    configuration: userSession.configuration,
    client_current_plan: userSession.currentPlan,
    current_url: userSession.currentUrl,
    current_url_name: userSession.currentUrlName,
    custom_event: userSession.customEvent,
    distinct_id: userSession.distinct_id,
    user_email: userSession.email,
    user_first_name: userSession.firstName,
    user_language: userSession.language,
    user_last_name: userSession.lastName,
    mp_lib: userSession.mp_lib,
    mp_sent_by_lib_version: userSession.mp_sent_by_lib_version,
    client_role: userSession.role,
    session_duration: userSession.sessionDuration,
    session_duration_min: userSession.sessionDurationMin,
    session_duration_sec: userSession.sessionDurationSec,
    skip_welcome: userSession.skipWelcome,
    user_status: userSession.status,
    time: userSession.time,
    token: userSession.token,
    user_id: userSession.userId,
    client_country_operation: userSession.operationCountry,
  });

  const initSessionMixPanel = (userSession) => {
    try {
      const token = import.meta.env.VITE_MIX_PANEL_TOKEN;
      mixpanel.init(token, {
        debug: !isProduction,
        persistence: 'localStorage',
        user_id: userSession.userId,
        distinct_id: userSession.userId,
        name: userSession.firstName,
      });
    } catch (e) {
      console.warn('[Analytics] initSessionMixPanel failed:', e);
    }
  };

  const trackMixPanelEvent = (name, data) => {
    try {
      let session = {};
      if (mixpanel.cookie === undefined) {
        const token = import.meta.env.VITE_MIX_PANEL_TOKEN;
        session = JSON.parse(localStorage.getItem(`mp_${token}_mixpanel`));
        if (!session) {
          session = getCurrentUser();
        }
        initSessionMixPanel(normalizeSessionData({ ...data, session }));
      } else {
        session = normalizeSessionData(data);
      }
      mixpanel.track(name, { ...data, session });
    } catch (e) {
      console.warn('[Analytics] trackMixPanelEvent failed:', e);
    }
  };

  const trackSessionStartEvent = (data) => {
    try {
      if (isProduction) {
        trackMixPanelEvent('Login Event', {
          ...lastPage,
          ...data,
        });
        const inTime = `login_time_${data.userId}`;
        localStorage.setItem(inTime, new Date().toISOString());
      }
    } catch (e) {
      console.warn('[Analytics] trackSessionStartEvent failed:', e);
    }
  };

  const trackSessionEndEvent = (message) => {
    try {
      if (isProduction) {
        const token = import.meta.env.VITE_MIX_PANEL_TOKEN;
        const data = JSON.parse(localStorage.getItem(`mp_${token}_mixpanel`));
        if (!data) {
          return;
        }
        const inTime = `login_time_${data.distinct_id}`;
        const dateInTime = new Date(localStorage.getItem(inTime));
        const sessionDurationMillis = Math.abs(new Date() - dateInTime);
        data.sessionDuration = millisToMinutesAndSeconds(sessionDurationMillis);
        data.sessionDurationMin = Math.floor(sessionDurationMillis / 60000);
        data.sessionDurationSec = ((sessionDurationMillis % 60000) / 1000).toFixed(0);
        trackMixPanelEvent(message, {
          ...lastPage,
          ...data,
        });
      }
    } catch (e) {
      console.warn('[Analytics] trackSessionEndEvent failed:', e);
    }
  };

  const trackPageView = (data) => {
    try {
      setLastPage(data);
      trackMixPanelEvent('Page View', data);
    } catch (e) {
      console.warn('[Analytics] trackPageView failed:', e);
    }
  };

  const trackPageEvent = (breadcrumbsItems) => {
    try {
      if (isProduction) {
        const currentUrlName = breadcrumbsItems.map(({ title }) => title).join(' - ');

        const currentUrl = window.location.href;
        const EXCLUDE_TRACK_URLS = ['/login'];
        const hasCurrentUrlName = currentUrlName && currentUrlName !== '';
        const isValidUrl = !EXCLUDE_TRACK_URLS.some((url) => currentUrl.includes(url));

        if (isValidUrl && hasCurrentUrlName) {
          trackPageView({
            currentUrl: window.location.href,
            currentUrlName,
            customEvent: 'pageView',
          });
        }
      }
    } catch (e) {
      console.warn('[Analytics] trackPageEvent failed:', e);
    }
  };

  const trackContinueEvent = (data) => {
    try {
      const eventName = data?.customEvent || 'Continue Event';
      trackMixPanelEvent(eventName, {
        ...lastPage,
        ...data,
      });
    } catch (e) {
      console.warn('[Analytics] trackContinueEvent failed:', e);
    }
  };

  const trackCancelEvent = (data) => {
    try {
      const eventName = data?.customEvent || 'Cancel Event';
      trackMixPanelEvent(eventName, {
        ...lastPage,
        ...data,
      });
    } catch (e) {
      console.warn('[Analytics] trackCancelEvent failed:', e);
    }
  };

  const trackErrorEvent = (data) => {
    try {
      const eventName = data?.customEvent || 'Error Event';
      trackMixPanelEvent(eventName, {
        ...lastPage,
        ...data,
      });
    } catch (e) {
      console.warn('[Analytics] trackErrorEvent failed:', e);
    }
  };

  const initHotjar = () => {

    // try {
    //   if (!Hotjar.isReady()) {
    //     const siteId = import.meta.env.VITE_HOTJAR_ID;
    //     const hotjarVersion = 6;

    //     Hotjar.init(siteId, hotjarVersion, {
    //       debug: !isProduction,
    //     });
    //   }
    // } catch {
    //   // App should not break if any analytics service fails
    // }
  };

  // HOTJAR
  const initSessionHotJar = (userSession) => {
    try {
      initHotjar();
      Hotjar.identify(userSession.userId, {
        first_name: userSession.firstName,
        last_name: userSession.lastName,
        email: userSession.email,
        language: userSession.language,
        status: userSession.status,
        client_id: userSession.clientId,
        client_company: userSession.company,
        client_paying: userSession.paying,
        client_role: userSession.role,
      });
    } catch (e) {
      console.warn('[Analytics] initSessionHotJar failed:', e);
    }
  };

  const recordCurrentPage = (keyPath, userSession) => {
    try {
      initSessionHotJar(userSession);

      const path = keyPath?.key || '';
      const isOverview = path.includes('overview');
      if (!isOverview && path !== lastPath) {
        Hotjar.event('start_recording');
        setLastPath(path);
      }
    } catch (e) {
      console.warn('[Analytics] recordCurrentPage failed:', e);
    }
  };

  // ALL
  const initAnalytics = (userSession) => {
    try {
      initSessionHotJar(userSession);
      initSessionMixPanel(userSession);
      mixpanel.identify(userSession.userId);
      mixpanel.register_once(normalizeSessionData(userSession));
      trackSessionStartEvent(userSession);
    } catch (e) {
      console.warn('[Analytics] initAnalytics failed:', e);
    }
  };

  const resetAnalytics = () => {
    try {
      if (isProduction) {
        trackSessionEndEvent('Logout Event');
        mixpanel.reset();
      }
    } catch (e) {
      console.warn('[Analytics] resetAnalytics failed:', e);
    }
  };

  const analyticsValue = useMemo(() => ({
    initAnalytics,
    resetAnalytics,
    mixPanel: {
      trackPageEvent,
      trackSessionEndEvent,
      trackCancelEvent,
      trackContinueEvent,
      trackErrorEvent,
    },
    hotJar: {
      initHotjar,
      recordCurrentPage,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lastPage]);

  return (
    <AnalyticsContext.Provider
      value={analyticsValue}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

AnalyticsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AnalyticsContextProvider;
